import { Nullable } from 'global';
import * as _ from 'lodash';
import {
  ProposalSaleType,
  LenderMarketRateInput,
  PaymentOptionsConfigurationTermsAndRatesInput,
  MarkupType,
} from '@trnsact/trnsact-shared-types';
import { prepareMenuOptionsForAPI } from './prepareMenuOptionsForAPI';
import { Markup, MarkupMethods, ConvertParams, Structures } from '../types';
import {
  CalculatePaymentCashVariables,
  CalculatePaymentFinanceVariables,
  CalculatePaymentMenuVariables,
} from '../api/types';
import { convertFeeFormValueToFee } from './convertFeeFormValueToFee';

export function collectFinanceVariablesForCalc({
  program,
  submission,
  saleDetails,
  proposalMenu,
  isExcludeEquipmentSales,
  financeProgramDescription,
  proposalProductConfigurationsInMenu,
}: ConvertParams): CalculatePaymentFinanceVariables {
  const prepareMenu: CalculatePaymentMenuVariables = {
    name: proposalMenu?.name ?? '',
    description: proposalMenu?.description ?? '',
    menuOptions: prepareMenuOptionsForAPI(
      proposalMenu?.menuOptions ?? [],
      financeProgramDescription,
      proposalProductConfigurationsInMenu
    ),
  };

  const terms: PaymentOptionsConfigurationTermsAndRatesInput[] = [];

  if (program.specifyRate && program.specifyRates.length > 0) {
    terms.push(...program.specifyRates.map(termValue => ({ term: Number(termValue.term), rate: +termValue.rate })));

    if (program.customTermTemporaryValue) {
      terms.push({
        term: +program.customTermTemporaryValue,
        rate: 0,
      });
    }
  } else {
    terms.push(
      ...[...program.terms, program.customTermTemporaryValue]
        .filter(Boolean)
        .map(t => ({ term: Number(t), rate: +program.commonRate }))
    );
  }

  let lenderMarketRatesToSend: LenderMarketRateInput[] = [];

  if (
    program.subsidyCapConfiguration?.lenderMarketRates &&
    program.subsidyCapConfiguration?.lenderMarketRates?.length > 0
  ) {
    try {
      if (program.specifyRate && program.specifyLenderMarketRates?.length > 0) {
        lenderMarketRatesToSend = program.specifyLenderMarketRates?.map((rate, index) => ({
          term: _.orderBy(terms, 'term', 'asc')[index]?.term ?? 0,
          rate: Number(
            program.specifyRate ? (program.specifyLenderMarketRates[index]?.rate ?? 0) : program.commonLenderMarketRate
          ),
        }));
      } else {
        lenderMarketRatesToSend = program.subsidyCapConfiguration?.lenderMarketRates?.map((rate, index) => ({
          term: _.orderBy(terms, 'term', 'asc')[index]?.term ?? 0,
          rate: Number(program.commonLenderMarketRate),
        }));
      }
    } catch (e: any) {
      console.error('collectFinanceVariablesForCalc: Error converting lender market rates', e.message);
      lenderMarketRatesToSend = [];
    }
  }

  let dealerMarkup: Nullable<Markup> = null;

  if (program.structure !== Structures.None && program.structure !== Structures.Subsidy) {
    if (program.markupMethod === MarkupMethods.MarkupPercentage) {
      dealerMarkup = { identifier: 'Dealer Markup', markupPercentage: Number(program.markupRate) };
    } else if (program.markupMethod === MarkupMethods.MarkupDollar) {
      dealerMarkup = { identifier: 'Dealer Markup', markupFlat: Number(program.markupRate) };
    }
  }

  let startDate: string | undefined = undefined;

  try {
    startDate = new Date(program.startDate).toISOString().split('T')[0];
  } catch (e: any) {
    console.error('convertProgramToCalculateVariables: Error converting start date to ISO string', e.message);
    startDate = new Date().toISOString().split('T')[0];
  }

  let amountFinancedForCalc = 0;

  if (!isExcludeEquipmentSales) {
    amountFinancedForCalc = saleDetails.financeAmount;
  }

  let downPaymentForCalc = saleDetails.downPayment.flat;
  /* Backend now handles thisif (program.minimumRequiredDownPayment && saleDetails.downPayment.percent < program.minimumRequiredDownPayment) {
    downPaymentForCalc = invoiceCalculations.calcDownPaymentFlat(
      saleDetails.invoiceTotal,
      program.minimumRequiredDownPayment
    );
    amountFinancedForCalc = saleDetails.invoiceTotal - downPaymentForCalc;
  } */

  const convertedProgram = {
    input: {
      finance: {
        startDate,
        salesTaxPercentage: 0,
        rateType: program.rateType,
        downPaymentAmount: downPaymentForCalc,
        minimumDownPaymentPercentage: program.minimumRequiredDownPayment,
        financeProgramType: program.programType,
        financeProgramName: program.programName,
        residualPercentage: Number(program.residual),
        maxApprovalAmount: submission.maxApprovalAmount,
        daysToFirstPayment: Number(program.daysToFirstPayment),
        numberOfAdvancePayments: Number(program.advancePayment),
        amountFinanced: amountFinancedForCalc,
        saleType: proposalMenu ? ProposalSaleType.FinanceWithAftermarketMenu : ProposalSaleType.FinanceOnly,
        menu: proposalMenu ? prepareMenu : undefined,
        customerRate:
          (program.structure !== Structures.None && program.markupMethod === MarkupMethods.CustomerRate) ||
          program.structure === Structures.Subsidy
            ? Number(program.markupRate)
            : null,
        dealerMarkup,
        paymentPeriodTiming: program.paymentCalculationMethod,
        markups: [{ identifier: 'Lender Markup', markupPercentage: 0 }],
        terms,
        flatFees: program.fee.filter(fee => fee.type === MarkupType.Flat).map(convertFeeFormValueToFee),
        percentageFees: program.fee.filter(fee => fee.type === MarkupType.Percentage).map(convertFeeFormValueToFee),
        isSubsidized: program.structure === Structures.Subsidy,
        subsidyCapConfiguration:
          program.structure === Structures.Subsidy && program.configureSubsidyCap
            ? { ...program.subsidyCapConfiguration, lenderMarketRates: lenderMarketRatesToSend }
            : null,
      },
    },
  };
  return convertedProgram;
}

export function collectCashVariablesForCalc({
  program,
  saleDetails,
  proposalMenu,
  isExcludeEquipmentSales,
  financeProgramDescription,
  proposalProductConfigurationsInMenu,
}: ConvertParams): CalculatePaymentCashVariables {
  const prepareMenu: CalculatePaymentMenuVariables = {
    name: proposalMenu?.name ?? '',
    description: proposalMenu?.description ?? '',
    menuOptions: prepareMenuOptionsForAPI(
      proposalMenu?.menuOptions ?? [],
      financeProgramDescription,
      proposalProductConfigurationsInMenu
    ),
  };

  return {
    input: {
      cash: {
        menu: prepareMenu,
        amountFinanced: isExcludeEquipmentSales ? 0 : saleDetails.financeAmount,
        saleType: ProposalSaleType.CashWithAftermarketMenu,
        flatFees: program.fee.filter(fee => fee.type === MarkupType.Flat).map(convertFeeFormValueToFee),
        percentageFees: program.fee.filter(fee => fee.type === MarkupType.Percentage).map(convertFeeFormValueToFee),
      },
    },
  };
}
